/**
 * Timeline State Hook
 *
 * React hook managing timeline state with undo/redo history stack.
 * Handles all timeline editing operations: add, remove, reorder, split, trim,
 * speed control, copy/paste, and type-aware selection.
 */

import { useReducer, useCallback, useMemo } from 'react'
import type {
  TimelineState,
  TimelineClip,
  TimelineAudioClip,
  TimelineAction,
  TimelineHistoryEntry,
} from './types'
import { computeTimelineDuration, preventSameTrackOverlaps } from './timelineUtils'
import { summarizeSelection } from './timelineSelection'
import { editClipToTimelineAudio, timelineAudioToEditClip } from '../../audio-edit/audioEditMath'
import { createNadDebug } from '@ponpon/api-client'

const timelineLog = createNadDebug('dw:timeline-playback:render-contract')
const featureLog = createNadDebug('dw:video-editor-improvements')
const magnetLog = createNadDebug('dw:video-editor:magnet')
const splitFrameLog = createNadDebug('dw:video-editor:split-frame')
const trackMuteLog = createNadDebug('dw:video-editor:track-mute')
const extractAudioLog = createNadDebug('dw:video-editor:extract-audio')
// RFC-0631 round-3 (Issue 5): per-track Remove button observability. Fires
// when a remove is requested, confirmed by the user, and applied to state.
const trackMgmtLog = createNadDebug('dw:video-editor:track-mgmt')

/** Default native fps used when source-fps probe is unsupported or pending. */
const DEFAULT_SOURCE_FPS = 30

export interface TimelineStateWithHistory {
  present: TimelineState
  past: TimelineHistoryEntry[]
  future: TimelineHistoryEntry[]
}

const MAX_HISTORY_DEPTH = 50

/** Maximum number of video clips allowed on the timeline */
const MAX_CLIPS = 99

/**
 * Create initial timeline state
 */
function createInitialState(): TimelineState {
  return {
    clips: [],
    audioClips: [],
    playheadPosition: 0,
    zoomLevel: 50, // 50 pixels per second default
    isPlaying: false,
    selectedClipIds: new Set<string>(),
    totalDuration: 0,
    videoTrackCount: 1,
    audioTrackCount: 1,
    copiedClip: null,
    copiedClipType: null,
    mutedVideoTracks: new Set<number>(),
    mutedAudioTracks: new Set<number>(),
    magnetEnabled: true,
  }
}

function withDuration(state: TimelineState): TimelineState {
  return {
    ...state,
    totalDuration: computeTimelineDuration(state.clips, state.audioClips),
  }
}

function withNonOverlappingVideo(state: TimelineState, editedClipId?: string): TimelineState {
  return withDuration({
    ...state,
    clips: preventSameTrackOverlaps(state.clips, editedClipId),
  })
}

function withNonOverlappingAudio(state: TimelineState, editedClipId?: string): TimelineState {
  return withDuration({
    ...state,
    audioClips: preventSameTrackOverlaps(state.audioClips, editedClipId),
  })
}

function withNonOverlappingTimeline(
  state: TimelineState,
  editedVideoClipId?: string,
  editedAudioClipId?: string
): TimelineState {
  return withDuration({
    ...state,
    clips: preventSameTrackOverlaps(state.clips, editedVideoClipId),
    audioClips: preventSameTrackOverlaps(state.audioClips, editedAudioClipId),
  })
}




/**
 * Timeline reducer handling all state mutations
 */
export function timelineReducer(
  state: TimelineStateWithHistory,
  action: TimelineAction
): TimelineStateWithHistory {
  const { present, past, future } = state

  // Helper to push current state to history
  const pushHistory = (newState: TimelineState, description: string): TimelineStateWithHistory => {
    const newPast = [
      ...past,
      {
        state: present,
        description,
        timestamp: Date.now(),
      },
    ].slice(-MAX_HISTORY_DEPTH)

    return {
      present: newState,
      past: newPast,
      future: [], // Clear future when new action performed
    }
  }

  switch (action.type) {
    case 'ADD_CLIP': {
      // Enforce 99-clip max
      if (present.clips.length >= MAX_CLIPS) {
        return state
      }
      const newClips = [...present.clips, action.clip].sort((a, b) => a.order - b.order)
      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, action.clip.id), `Add clip: ${action.clip.label}`)
    }

    case 'REMOVE_CLIP': {
      const newClips = present.clips.filter((c) => c.id !== action.clipId)
      // Reorder remaining clips
      const reorderedClips = newClips.map((clip, index) => ({ ...clip, order: index }))
      const nextSelected = new Set(present.selectedClipIds)
      nextSelected.delete(action.clipId)
      const newState = {
        ...present,
        clips: reorderedClips,
        audioClips: present.audioClips,
        selectedClipIds: nextSelected,
      }
      return pushHistory(withDuration(newState), `Remove clip: ${action.clipId}`)
    }

    case 'REORDER_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const otherClips = present.clips.filter((c) => c.id !== action.clipId)

      // Insert at new position
      const newClips = [
        ...otherClips.slice(0, action.newOrder),
        clip,
        ...otherClips.slice(action.newOrder),
      ]

      // Update order indices
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))

      const newState = {
        ...present,
        clips: reorderedClips,
      }
      return pushHistory(newState, `Reorder clip: ${clip.label} to position ${action.newOrder}`)
    }

    case 'SPLIT_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const playheadPosition = action.splitTime

      // Calculate split position relative to clip start
      const rawRelativeTime = playheadPosition - clip.startOffset

      // RFC-0651 #4: snap the split to the source clip's native frame
      // boundary so the resulting cut lands cleanly on a frame edge.
      // Frame N stays with the first clip; frame N+1 is the first frame of
      // the second clip (relativeTime is multiplied by `clip.speed` further
      // down when computing the actual source-time offset).
      const sourceFps = DEFAULT_SOURCE_FPS
      const frameDur = 1 / sourceFps
      const snappedRelativeTime = Math.floor(rawRelativeTime / frameDur) * frameDur
      const relativeTime = snappedRelativeTime
      const secondClipRelativeStart = relativeTime + frameDur

      // Validate split point is within clip bounds
      if (relativeTime <= 0 || relativeTime >= clip.effectiveDuration || secondClipRelativeStart >= clip.effectiveDuration) {
        // Split point outside valid range
        return state
      }

      splitFrameLog({
        event: 'split-snapped',
        clipId: action.clipId,
        sourceFps,
        playhead: playheadPosition,
        rawRelativeTime,
        snappedRelativeTime,
      })

      // Create two new clips (speed-aware: convert relative time back to source time)
      const sourceRelativeTime = relativeTime * clip.speed
      const sourceFrameDurInSourceSpace = frameDur * clip.speed

      const firstClip: TimelineClip = {
        ...clip,
        id: `${clip.id}-split-1`,
        trimEnd: clip.trimEnd + (clip.originalDuration - clip.trimStart - clip.trimEnd - sourceRelativeTime),
        effectiveDuration: relativeTime,
        label: `${clip.label} (1)`,
      }

      const secondClip: TimelineClip = {
        ...clip,
        id: `${clip.id}-split-2`,
        // +frame in SOURCE space so frame N+1 starts the second clip.
        trimStart: clip.trimStart + sourceRelativeTime + sourceFrameDurInSourceSpace,
        effectiveDuration: clip.effectiveDuration - relativeTime - frameDur,
        startOffset: clip.startOffset + relativeTime + frameDur,
        order: clip.order + 1,
        label: `${clip.label} (2)`,
      }

      // Replace original clip with two new clips
      const newClips = [
        ...present.clips.slice(0, clipIndex),
        firstClip,
        secondClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      // Update order indices for clips after the split
      const reorderedClips = newClips.map((c, index) => ({ ...c, order: index }))

      const newState = {
        ...present,
        clips: reorderedClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, secondClip.id), `Split clip: ${clip.label} at ${playheadPosition}s`)
    }

    case 'TRIM_CLIP': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      // When trim-start drag couples startOffset (Issue 1), the handler passes
      // the new startOffset (may be temporarily negative — NORMALIZE_TIMELINE_ORIGIN
      // shifts it back on mouseUp).
      const startOffset = action.startOffset ?? clip.startOffset
      const newClip = {
        ...clip,
        trimStart,
        trimEnd,
        startOffset,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      featureLog({
        event: 'trim-start-with-offset',
        clipId: action.clipId,
        newTrimStart: trimStart,
        newStartOffset: startOffset,
      })

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(
        withNonOverlappingVideo(newState, action.clipId),
        `Trim clip: ${clip.label} (start: ${action.trimStart}, end: ${action.trimEnd})`
      )
    }

    case 'TRIM_CLIP_PREVIEW': {
      // Same as TRIM_CLIP but without pushing to history (used during drag)
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const startOffset = action.startOffset ?? clip.startOffset
      const newClip = {
        ...clip,
        trimStart,
        trimEnd,
        startOffset,
        effectiveDuration: (clip.originalDuration - trimStart - trimEnd) / clip.speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      // Allow the edited clip's startOffset to go negative during drag;
      // NORMALIZE_TIMELINE_ORIGIN on mouseUp shifts the whole timeline right.
      return {
        ...state,
        present: {
          ...present,
          ...withDuration({
            ...present,
            clips: preventSameTrackOverlaps(newClips, action.clipId, {
              allowNegativeForEditedClip: true,
            }),
          }),
        },
      }
    }

    case 'SET_CLIP_OFFSET': {
      const newClips = present.clips.map((clip) =>
        clip.id === action.clipId
          ? { ...clip, startOffset: Math.max(0, action.startOffset) }
          : clip
      )
      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, action.clipId), `Set video offset: ${action.startOffset}s`)
    }

    case 'SET_CLIP_TRACK_INDEX': {
      const trackIndex = Math.max(0, Math.min(present.videoTrackCount - 1, action.trackIndex))
      const newClips = present.clips.map((clip) =>
        clip.id === action.clipId ? { ...clip, trackIndex } : clip
      )
      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, action.clipId), `Move video clip to track ${trackIndex + 1}`)
    }

    case 'SELECT_CLIP': {
      // Replace selection (existing behavior — non-shift click).
      const next = action.clipId === null
        ? new Set<string>()
        : new Set<string>([action.clipId])
      return {
        ...state,
        present: {
          ...present,
          selectedClipIds: next,
        },
      }
    }

    case 'TOGGLE_CLIP_SELECTION': {
      // Shift-click: add/remove the clip from the current selection.
      // Cross-type (video + audio in the same selection) is ALLOWED.
      const next = new Set(present.selectedClipIds)
      if (next.has(action.clipId)) {
        next.delete(action.clipId)
      } else {
        next.add(action.clipId)
      }
      return {
        ...state,
        present: {
          ...present,
          selectedClipIds: next,
        },
      }
    }

    case 'CLEAR_SELECTION': {
      if (present.selectedClipIds.size === 0) return state
      return {
        ...state,
        present: {
          ...present,
          selectedClipIds: new Set<string>(),
        },
      }
    }

    case 'SET_PLAYHEAD': {
      // Playhead movement doesn't go into history
      return {
        ...state,
        present: {
          ...present,
          playheadPosition: Math.max(0, Math.min(action.position, present.totalDuration)),
        },
      }
    }

    case 'SET_ZOOM': {
      // Zoom doesn't go into history
      return {
        ...state,
        present: {
          ...present,
          zoomLevel: Math.max(10, Math.min(200, action.zoomLevel)), // 10-200 pixels per second
        },
      }
    }

    case 'PLAY': {
      // If the playhead is already at (or within 100ms of) the end of
      // the timeline, snap it back to the start before playing —
      // matches standard media-player behaviour (YouTube, QuickTime,
      // etc.): press play after playback completes → restart from 0.
      //
      // Why a 100ms tolerance instead of strict equality: the playback
      // animation loop in useTimelinePlayback advances the playhead in
      // frame-aligned increments and the natural end-of-clip may land
      // a few ms shy of totalDuration. Without the tolerance, those
      // sessions would persist a "near-end" playhead that doesn't
      // trigger the reset.
      //
      // The previous behaviour persisted the end-of-timeline playhead
      // to YDoc, so re-opening the editor surfaced the cursor at
      // 01:16 with the user having to drag-to-start before play
      // worked — user-reported as "player always seeks to end".
      const isAtEnd =
        present.totalDuration > 0 &&
        present.playheadPosition >= present.totalDuration - 0.1
      return {
        ...state,
        present: {
          ...present,
          isPlaying: true,
          playheadPosition: isAtEnd ? 0 : present.playheadPosition,
        },
      }
    }

    case 'PAUSE': {
      return {
        ...state,
        present: {
          ...present,
          isPlaying: false,
        },
      }
    }

    case 'ADD_AUDIO_CLIP': {
      const newAudioClips = [...present.audioClips, action.clip]
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clip.id), `Add audio clip`)
    }

    case 'ADD_AUDIO_CLIPS': {
      if (action.clips.length === 0) {
        return state
      }
      const newAudioClips = [...present.audioClips, ...action.clips]
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(
        withNonOverlappingAudio(newState, action.clips[0].id),
        `Add ${action.clips.length} audio clip${action.clips.length === 1 ? '' : 's'}`
      )
    }

    case 'REMOVE_AUDIO_CLIP': {
      const newAudioClips = present.audioClips.filter((c) => c.id !== action.clipId)
      const nextSelected = new Set(present.selectedClipIds)
      nextSelected.delete(action.clipId)
      const newState = {
        ...present,
        audioClips: newAudioClips,
        selectedClipIds: nextSelected,
      }
      return pushHistory(withDuration(newState), `Remove audio clip`)
    }

    case 'SET_AUDIO_VOLUME': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        volume: Math.max(0, Math.min(1, action.volume)),
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Set audio volume: ${action.volume}`)
    }

    case 'TOGGLE_AUDIO_MUTE': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        muted: !newAudioClips[clipIndex].muted,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Toggle audio mute`)
    }

    case 'SET_AUDIO_OFFSET': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...newAudioClips[clipIndex],
        startOffset: Math.max(0, action.startOffset),
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Set audio offset: ${action.startOffset}s`)
    }

    case 'SET_AUDIO_TRACK_INDEX': {
      const trackIndex = Math.max(0, Math.min(present.audioTrackCount - 1, action.trackIndex))
      const newAudioClips = present.audioClips.map((clip) =>
        clip.id === action.clipId ? { ...clip, trackIndex } : clip
      )
      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Move audio clip to track ${trackIndex + 1}`)
    }

    case 'TRIM_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const editClip = timelineAudioToEditClip({
        ...clip,
        trimStart,
        trimEnd,
        startOffset: action.startOffset ?? clip.startOffset,
      })
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = editClipToTimelineAudio(editClip, clip)

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Trim audio clip`)
    }

    case 'TRIM_AUDIO_CLIP_PREVIEW': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const trimStart = Math.max(0, action.trimStart)
      const trimEnd = Math.max(0, action.trimEnd)
      const editClip = timelineAudioToEditClip({
        ...clip,
        trimStart,
        trimEnd,
        startOffset: action.startOffset ?? clip.startOffset,
      })
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = editClipToTimelineAudio(editClip, clip)

      return {
        ...state,
        present: {
          ...present,
          audioClips: newAudioClips,
          totalDuration: computeTimelineDuration(present.clips, newAudioClips),
        },
      }
    }

    case 'SET_CLIP_SPEED': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const speed = Math.max(0.1, action.speed) // Minimum speed 0.1x
      const newClip = {
        ...clip,
        speed,
        effectiveDuration: (clip.originalDuration - clip.trimStart - clip.trimEnd) / speed,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(withNonOverlappingVideo(newState, action.clipId), `Set clip speed: ${speed}x`)
    }

    case 'SET_CLIP_VOLUME': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const newClip = {
        ...clip,
        volume: Math.max(0, Math.min(1, action.volume)),
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Set video clip volume: ${action.volume}`)
    }

    case 'SET_CLIP_FADE_OUT': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      // Clamp to [0, effectiveDuration]. Effective duration is already speed-adjusted.
      const maxFade = Math.max(0, clip.effectiveDuration)
      const clamped = Math.max(0, Math.min(maxFade, action.fadeOutS))
      // No-op if value is unchanged to avoid pushing useless history entries.
      if (Math.abs(clamped - (clip.fadeOutS ?? 0)) < 1e-4) return state

      const newClip = {
        ...clip,
        fadeOutS: clamped,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Set video clip fade-out: ${clamped}s`)
    }

    case 'SET_CLIP_FADE_IN': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const maxFade = Math.max(0, clip.effectiveDuration)
      const clamped = Math.max(0, Math.min(maxFade, action.fadeInS))
      if (Math.abs(clamped - (clip.fadeInS ?? 0)) < 1e-4) return state

      const newClip = {
        ...clip,
        fadeInS: clamped,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Set video clip fade-in: ${clamped}s`)
    }

    case 'NORMALIZE_TIMELINE_ORIGIN': {
      // After a trim-start drag may have pulled a clip into negative time,
      // shift ALL clips (video AND audio) by the same delta so the leftmost
      // clip starts at 0. Single global pass — preserves cross-track A/V sync.
      let minStart = Infinity
      for (const c of present.clips) {
        if (c.startOffset < minStart) minStart = c.startOffset
      }
      for (const c of present.audioClips) {
        if (c.startOffset < minStart) minStart = c.startOffset
      }
      if (!Number.isFinite(minStart) || minStart >= 0) return state

      const shift = -minStart
      const newClips = present.clips.map((c) => ({ ...c, startOffset: c.startOffset + shift }))
      const newAudioClips = present.audioClips.map((c) => ({ ...c, startOffset: c.startOffset + shift }))

      featureLog({
        event: 'normalize-timeline-origin',
        shiftSeconds: Number(shift.toFixed(3)),
        affectedClipCount: newClips.length + newAudioClips.length,
      })

      const newState = {
        ...present,
        clips: newClips,
        audioClips: newAudioClips,
      }
      // Use the modified withDuration to capture the new total; do NOT push a
      // separate history entry — caller (TRIM_CLIP commit) already pushed one.
      return {
        ...state,
        present: withDuration(newState),
      }
    }

    case 'TOGGLE_CLIP_MUTE': {
      const clipIndex = present.clips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.clips[clipIndex]
      const newClip = {
        ...clip,
        muted: !clip.muted,
      }

      const newClips = [
        ...present.clips.slice(0, clipIndex),
        newClip,
        ...present.clips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        clips: newClips,
      }
      return pushHistory(newState, `Toggle video clip mute`)
    }

    case 'SET_AUDIO_SPEED': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const speed = Math.max(0.1, action.speed) // Minimum speed 0.1x
      const newAudioClips = [...present.audioClips]
      newAudioClips[clipIndex] = {
        ...clip,
        speed,
        effectiveDuration: (clip.originalDuration - clip.trimStart - clip.trimEnd) / speed,
      }

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, action.clipId), `Set audio speed: ${speed}x`)
    }

    case 'SPLIT_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const splitTime = action.splitTime

      // Calculate relative split position within the audio clip
      const relativeTime = splitTime - clip.startOffset

      // Validate split point is within clip bounds
      if (relativeTime <= 0 || relativeTime >= clip.effectiveDuration) {
        return state
      }

      // Convert relative time back to source time (speed-aware)
      const sourceRelativeTime = relativeTime * clip.speed

      const firstClip: TimelineAudioClip = {
        ...clip,
        id: `${clip.id}-split-1`,
        trimEnd: clip.trimEnd + (clip.originalDuration - clip.trimStart - clip.trimEnd - sourceRelativeTime),
        effectiveDuration: relativeTime,
      }

      const secondClip: TimelineAudioClip = {
        ...clip,
        id: `${clip.id}-split-2`,
        trimStart: clip.trimStart + sourceRelativeTime,
        effectiveDuration: clip.effectiveDuration - relativeTime,
        startOffset: clip.startOffset + relativeTime,
      }

      const newAudioClips = [
        ...present.audioClips.slice(0, clipIndex),
        firstClip,
        secondClip,
        ...present.audioClips.slice(clipIndex + 1),
      ]

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(withNonOverlappingAudio(newState, secondClip.id), `Split audio clip at ${splitTime}s`)
    }

    case 'REORDER_AUDIO_CLIP': {
      const clipIndex = present.audioClips.findIndex((c) => c.id === action.clipId)
      if (clipIndex === -1) return state

      const clip = present.audioClips[clipIndex]
      const otherClips = present.audioClips.filter((c) => c.id !== action.clipId)

      // Insert at new position
      const newAudioClips = [
        ...otherClips.slice(0, action.newOrder),
        clip,
        ...otherClips.slice(action.newOrder),
      ]

      const newState = {
        ...present,
        audioClips: newAudioClips,
      }
      return pushHistory(newState, `Reorder audio clip to position ${action.newOrder}`)
    }

    case 'COPY_CLIP': {
      // Copy the primary (first) selected clip into the clipboard. Multi-clip
      // copy isn't supported in v1 — paste replays a single clip at the
      // playhead. Skip if nothing is selected.
      const summary = summarizeSelection(present)
      if (!summary.primaryId || summary.type === 'mixed' || summary.type === null) {
        return state
      }

      let clipToCopy: TimelineClip | TimelineAudioClip | null = null
      if (summary.type === 'video') {
        clipToCopy = present.clips.find((c) => c.id === summary.primaryId) ?? null
      } else {
        clipToCopy = present.audioClips.find((c) => c.id === summary.primaryId) ?? null
      }

      if (!clipToCopy) return state

      return {
        ...state,
        present: {
          ...present,
          copiedClip: { ...clipToCopy },
          copiedClipType: summary.type,
        },
      }
    }

    case 'PASTE_CLIP': {
      if (!present.copiedClip || !present.copiedClipType) {
        return state
      }

      if (present.copiedClipType === 'video') {
        // Enforce 99-clip max
        if (present.clips.length >= MAX_CLIPS) {
          return state
        }

        const copiedVideoClip = present.copiedClip
        if ('role' in copiedVideoClip) return state
        const newClip: TimelineClip = {
          ...copiedVideoClip,
          id: crypto.randomUUID(),
          order: present.clips.length,
          startOffset: present.playheadPosition,
          label: `${copiedVideoClip.label} (copy)`,
        }

        const newClips = [...present.clips, newClip].sort((a, b) => a.order - b.order)
        const newState = {
          ...present,
          clips: newClips,
        }
        return pushHistory(withNonOverlappingVideo(newState, newClip.id), `Paste clip: ${newClip.label}`)
      } else {
        const copiedAudioClip = present.copiedClip
        if (!('role' in copiedAudioClip)) return state
        const newClip: TimelineAudioClip = {
          ...copiedAudioClip,
          id: crypto.randomUUID(),
          startOffset: present.playheadPosition,
        }

        const newAudioClips = [...present.audioClips, newClip]
        const newState = {
          ...present,
          audioClips: newAudioClips,
        }
        return pushHistory(withNonOverlappingAudio(newState, newClip.id), `Paste audio clip`)
      }
    }

    case 'DELETE_SELECTED_CLIP': {
      if (present.selectedClipIds.size === 0) return state

      const newClips = present.clips.filter((c) => !present.selectedClipIds.has(c.id))
      const reorderedClips = newClips.map((clip, index) => ({ ...clip, order: index }))
      const newAudioClips = present.audioClips.filter((c) => !present.selectedClipIds.has(c.id))
      const newState = {
        ...present,
        clips: reorderedClips,
        audioClips: newAudioClips,
        selectedClipIds: new Set<string>(),
      }
      return pushHistory(withDuration(newState), `Delete ${present.selectedClipIds.size} selected clip(s)`)
    }

    case 'RIPPLE_DELETE_SELECTED':
    case 'CLOSE_ALL_GAPS': {
      // Per-track gap closure (Issue 3). For every track (video AND audio),
      // sort clips by startOffset and pack them flush left-to-right starting
      // from 0. No clip is deleted. Cross-track sync may not be preserved
      // (each track collapses independently — by spec).
      const closeGapsOnTrack = <T extends { startOffset: number; effectiveDuration: number; trackIndex: number }>(
        clips: readonly T[],
      ): { next: T[]; gapsClosed: number } => {
        if (clips.length === 0) return { next: [], gapsClosed: 0 }
        const sorted = [...clips].sort((a, b) => a.startOffset - b.startOffset)
        const next: T[] = []
        let cursor = 0
        let gapsClosed = 0
        for (const c of sorted) {
          if (c.startOffset > cursor + 1e-6) {
            next.push({ ...c, startOffset: cursor })
            gapsClosed += 1
          } else {
            next.push(c)
          }
          cursor = (next[next.length - 1].startOffset) + c.effectiveDuration
        }
        return { next, gapsClosed }
      }

      const groupByTrack = <T extends { trackIndex: number }>(clips: readonly T[]): Map<number, T[]> => {
        const out = new Map<number, T[]>()
        for (const c of clips) {
          const arr = out.get(c.trackIndex) ?? []
          arr.push(c)
          out.set(c.trackIndex, arr)
        }
        return out
      }

      const perTrackGapsClosed: Record<string, number> = {}
      let totalGapsClosed = 0

      const videoByTrack = groupByTrack(present.clips)
      const newVideoClips: TimelineClip[] = []
      for (const [trackIndex, trackClips] of videoByTrack.entries()) {
        const { next, gapsClosed } = closeGapsOnTrack(trackClips)
        if (gapsClosed > 0) {
          perTrackGapsClosed[`v${trackIndex}`] = gapsClosed
          totalGapsClosed += gapsClosed
        }
        newVideoClips.push(...next)
      }

      const audioByTrack = groupByTrack(present.audioClips)
      const newAudioClips: TimelineAudioClip[] = []
      for (const [trackIndex, trackClips] of audioByTrack.entries()) {
        const { next, gapsClosed } = closeGapsOnTrack(trackClips)
        if (gapsClosed > 0) {
          perTrackGapsClosed[`a${trackIndex}`] = gapsClosed
          totalGapsClosed += gapsClosed
        }
        newAudioClips.push(...next)
      }

      // Clip count invariant: must not change.
      const clipCountBefore = present.clips.length + present.audioClips.length
      const clipCountAfter = newVideoClips.length + newAudioClips.length

      featureLog({
        event: 'close-all-gaps-applied',
        perTrackGapsClosed,
        totalGapsClosed,
        clipCountBefore,
        clipCountAfter,
      })

      // Preserve per-clip order across the array (we grouped by track).
      const newVideoById = new Map(newVideoClips.map((c) => [c.id, c]))
      const reassembledVideo = present.clips.map((c) => newVideoById.get(c.id) ?? c)
      const newAudioById = new Map(newAudioClips.map((c) => [c.id, c]))
      const reassembledAudio = present.audioClips.map((c) => newAudioById.get(c.id) ?? c)

      const newState = {
        ...present,
        clips: reassembledVideo,
        audioClips: reassembledAudio,
      }

      if (totalGapsClosed === 0) {
        return state
      }

      return pushHistory(
        withNonOverlappingTimeline(newState),
        `Close all gaps (${totalGapsClosed} gap(s))`,
      )
    }

    case 'MULTI_DRAG_OFFSET': {
      if (present.selectedClipIds.size === 0) return state

      const { deltaSec, trackDelta } = action
      // Compute the minimum startOffset across selected clips so we can clamp
      // delta so no clip goes negative — every clip moves by exactly the same
      // amount, preserving inter-clip gaps within the multi-selection.
      let minStart = Infinity
      const collect = (s: number) => {
        if (s < minStart) minStart = s
      }
      for (const c of present.clips) {
        if (present.selectedClipIds.has(c.id)) collect(c.startOffset)
      }
      for (const c of present.audioClips) {
        if (present.selectedClipIds.has(c.id)) collect(c.startOffset)
      }
      if (!Number.isFinite(minStart)) return state

      const clampedDelta = Math.max(deltaSec, -minStart)

      const td = trackDelta ?? 0
      const newClips = present.clips.map((c) => {
        if (!present.selectedClipIds.has(c.id)) return c
        const nextTrack = td !== 0
          ? Math.max(0, Math.min(present.videoTrackCount - 1, c.trackIndex + td))
          : c.trackIndex
        return {
          ...c,
          startOffset: Math.max(0, c.startOffset + clampedDelta),
          trackIndex: nextTrack,
        }
      })
      const newAudioClips = present.audioClips.map((c) => {
        if (!present.selectedClipIds.has(c.id)) return c
        const nextTrack = td !== 0
          ? Math.max(0, Math.min(present.audioTrackCount - 1, c.trackIndex + td))
          : c.trackIndex
        return {
          ...c,
          startOffset: Math.max(0, c.startOffset + clampedDelta),
          trackIndex: nextTrack,
        }
      })

      const newState = {
        ...present,
        clips: newClips,
        audioClips: newAudioClips,
      }
      timelineLog({
        event: 'multi-drag-commit',
        clipIds: Array.from(present.selectedClipIds),
        deltaSec: Number(clampedDelta.toFixed(3)),
        trackDelta: td,
      })
      return pushHistory(
        withNonOverlappingTimeline(newState),
        `Multi-drag ${present.selectedClipIds.size} clip(s) by ${clampedDelta.toFixed(2)}s`,
      )
    }

    case 'ADD_VIDEO_TRACK': {
      return pushHistory(
        {
          ...present,
          videoTrackCount: present.videoTrackCount + 1,
        },
        `Add video track`
      )
    }

    case 'ADD_AUDIO_TRACK': {
      return pushHistory(
        {
          ...present,
          audioTrackCount: present.audioTrackCount + 1,
        },
        `Add audio track`
      )
    }

    // RFC-0631 round-3 (Issue 5): remove a video sub-track. Track 0 (Main
    // track) is protected; the count never drops below 1. When the track
    // has clips and `deleteClips` is true, those clips are dropped; when
    // false, the action is a safe no-op. Higher tracks shift down by 1 and
    // the muted-track set is re-indexed in the same step.
    case 'REMOVE_VIDEO_TRACK': {
      if (action.trackIndex === 0) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'video',
          trackIndex: action.trackIndex,
          reason: 'main-track-protected',
        })
        return state
      }
      if (present.videoTrackCount <= 1) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'video',
          trackIndex: action.trackIndex,
          reason: 'last-track',
        })
        return state
      }
      const targetClips = present.clips.filter((c) => c.trackIndex === action.trackIndex)
      if (targetClips.length > 0 && !action.deleteClips) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'video',
          trackIndex: action.trackIndex,
          reason: 'clips-present-without-confirm',
          clipCount: targetClips.length,
        })
        return state
      }
      const filteredClips = action.deleteClips
        ? present.clips.filter((c) => c.trackIndex !== action.trackIndex)
        : present.clips
      const shiftedClips = filteredClips.map((c) =>
        c.trackIndex > action.trackIndex ? { ...c, trackIndex: c.trackIndex - 1 } : c
      )
      const nextMuted = new Set<number>()
      for (const idx of present.mutedVideoTracks) {
        if (idx === action.trackIndex) continue
        nextMuted.add(idx > action.trackIndex ? idx - 1 : idx)
      }
      const newTrackCount = present.videoTrackCount - 1
      trackMgmtLog({
        event: 'track-remove-applied',
        kind: 'video',
        trackIndex: action.trackIndex,
        deleteClips: action.deleteClips,
        clipCount: targetClips.length,
        newTrackCount,
      })
      return pushHistory(
        withDuration({
          ...present,
          clips: shiftedClips,
          videoTrackCount: newTrackCount,
          mutedVideoTracks: nextMuted,
        }),
        `Remove video track ${action.trackIndex + 1}`
      )
    }

    case 'REMOVE_AUDIO_TRACK': {
      if (present.audioTrackCount <= 1) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'audio',
          trackIndex: action.trackIndex,
          reason: 'last-track',
        })
        return state
      }
      const targetClips = present.audioClips.filter((c) => c.trackIndex === action.trackIndex)
      if (targetClips.length > 0 && !action.deleteClips) {
        trackMgmtLog({
          event: 'track-remove-rejected',
          kind: 'audio',
          trackIndex: action.trackIndex,
          reason: 'clips-present-without-confirm',
          clipCount: targetClips.length,
        })
        return state
      }
      const filteredClips = action.deleteClips
        ? present.audioClips.filter((c) => c.trackIndex !== action.trackIndex)
        : present.audioClips
      const shiftedClips = filteredClips.map((c) =>
        c.trackIndex > action.trackIndex ? { ...c, trackIndex: c.trackIndex - 1 } : c
      )
      const nextMuted = new Set<number>()
      for (const idx of present.mutedAudioTracks) {
        if (idx === action.trackIndex) continue
        nextMuted.add(idx > action.trackIndex ? idx - 1 : idx)
      }
      const newTrackCount = present.audioTrackCount - 1
      trackMgmtLog({
        event: 'track-remove-applied',
        kind: 'audio',
        trackIndex: action.trackIndex,
        deleteClips: action.deleteClips,
        clipCount: targetClips.length,
        newTrackCount,
      })
      return pushHistory(
        withDuration({
          ...present,
          audioClips: shiftedClips,
          audioTrackCount: newTrackCount,
          mutedAudioTracks: nextMuted,
        }),
        `Remove audio track ${action.trackIndex + 1}`
      )
    }

    case 'SPLIT_SELECTED_CLIP': {
      // Split is undefined for multi-selection — only act when exactly one
      // clip is selected.
      if (present.selectedClipIds.size !== 1) {
        return state
      }
      const summary = summarizeSelection(present)
      if (!summary.primaryId || summary.type === null || summary.type === 'mixed') {
        return state
      }

      if (summary.type === 'video') {
        return timelineReducer(state, {
          type: 'SPLIT_CLIP',
          clipId: summary.primaryId,
          splitTime: present.playheadPosition,
        })
      } else {
        return timelineReducer(state, {
          type: 'SPLIT_AUDIO_CLIP',
          clipId: summary.primaryId,
          splitTime: present.playheadPosition,
        })
      }
    }

    case 'TOGGLE_TRACK_MUTE': {
      const { trackKind, trackIndex } = action
      const sourceSet = trackKind === 'video' ? present.mutedVideoTracks : present.mutedAudioTracks
      const next = new Set(sourceSet)
      if (next.has(trackIndex)) next.delete(trackIndex)
      else next.add(trackIndex)
      trackMuteLog({
        event: 'toggle-track-mute',
        trackKind,
        trackIndex,
        muted: next.has(trackIndex),
      })
      const newPresent: TimelineState =
        trackKind === 'video'
          ? { ...present, mutedVideoTracks: next }
          : { ...present, mutedAudioTracks: next }
      return pushHistory(newPresent, `Toggle ${trackKind} track ${trackIndex} mute`)
    }

    case 'SET_MAGNET': {
      if (present.magnetEnabled === action.enabled) return state
      magnetLog({ event: 'magnet-toggle', enabled: action.enabled })
      // Persistent-but-non-historical: skip the history push so undo doesn't
      // toggle the magnet button (matches typical app-preference behavior).
      return { ...state, present: { ...present, magnetEnabled: action.enabled } }
    }

    case 'MAGNET_PACK_TRACK0': {
      // Pack only trackIndex === 0 video clips left-to-right starting at 0.
      const track0 = present.clips.filter((c) => c.trackIndex === 0)
      if (track0.length === 0) return state
      const sorted = [...track0].sort((a, b) => a.startOffset - b.startOffset)
      const packed: TimelineClip[] = []
      let cursor = 0
      let movedCount = 0
      for (const c of sorted) {
        if (Math.abs(c.startOffset - cursor) > 1e-6) {
          packed.push({ ...c, startOffset: cursor })
          movedCount += 1
        } else {
          packed.push(c)
        }
        cursor = packed[packed.length - 1].startOffset + c.effectiveDuration
      }
      if (movedCount === 0) return state
      magnetLog({
        event: 'magnet-pack-track0',
        clipsPackedCount: movedCount,
        trackClipCount: track0.length,
      })
      const byId = new Map(packed.map((c) => [c.id, c]))
      const newClips = present.clips.map((c) => byId.get(c.id) ?? c)
      // Skip history: magnet pack is an automatic follow-on to whatever
      // history-pushing op just triggered it (trim / move / split commit).
      return {
        ...state,
        present: withDuration({ ...present, clips: newClips }),
      }
    }

    case 'EXTRACT_AUDIO_FROM_CLIP': {
      // Insert the new audio clip on a freshly-numbered track immediately
      // below the source video track. The action precomputes audioClip and
      // newAudioTrackIndex on the client side (Web Audio decode → WAV
      // encode → upload → relpath). The reducer just renumbers existing
      // audio tracks if needed and pushes the new clip.
      const newIndex = action.newAudioTrackIndex
      // Shift any existing audio clips at or below newIndex down by 1 to
      // make room for the new track.
      const shiftedAudio = present.audioClips.map((ac) =>
        ac.trackIndex >= newIndex ? { ...ac, trackIndex: ac.trackIndex + 1 } : ac,
      )
      const newAudioClip: TimelineAudioClip = {
        ...action.audioClip,
        trackIndex: newIndex,
      }
      const nextAudio = [...shiftedAudio, newAudioClip]
      const newAudioTrackCount = Math.max(
        present.audioTrackCount + 1,
        ...nextAudio.map((a) => a.trackIndex + 1),
      )
      extractAudioLog({
        event: 'extract-audio-inserted',
        sourceClipId: action.sourceClipId,
        audioClipId: newAudioClip.id,
        durationSec: newAudioClip.effectiveDuration,
        newAudioTrackIndex: newIndex,
      })
      return pushHistory(
        withDuration({
          ...present,
          audioClips: nextAudio,
          audioTrackCount: newAudioTrackCount,
        }),
        `Extract audio from ${action.sourceClipId}`,
      )
    }

    case 'UNDO': {
      if (past.length === 0) return state

      const previous = past[past.length - 1]
      const newPast = past.slice(0, -1)
      const newFuture = [
        {
          state: present,
          description: 'undo',
          timestamp: Date.now(),
        },
        ...future,
      ]

      return {
        present: previous.state,
        past: newPast,
        future: newFuture,
      }
    }

    case 'REDO': {
      if (future.length === 0) return state

      const next = future[0]
      const newFuture = future.slice(1)
      const newPast = [
        ...past,
        {
          state: present,
          description: 'redo',
          timestamp: Date.now(),
        },
      ]

      return {
        present: next.state,
        past: newPast,
        future: newFuture,
      }
    }

    case 'RESTORE_STATE': {
      // Used for loading from Yjs or initial state
      return {
        present: withNonOverlappingTimeline(action.state),
        past: [],
        future: [],
      }
    }

    default:
      return state
  }
}

/**
 * Hook for managing timeline state with undo/redo
 */
export function useTimelineState(initialState?: TimelineState) {
  const [state, dispatch] = useReducer(timelineReducer, {
    present: initialState ? withNonOverlappingTimeline(initialState) : createInitialState(),
    past: [],
    future: [],
  })

  // Action creators
  const addClip = useCallback((clip: TimelineClip) => {
    dispatch({ type: 'ADD_CLIP', clip })
  }, [])

  const removeClip = useCallback((clipId: string) => {
    dispatch({ type: 'REMOVE_CLIP', clipId })
  }, [])

  const reorderClip = useCallback((clipId: string, newOrder: number) => {
    dispatch({ type: 'REORDER_CLIP', clipId, newOrder })
  }, [])

  const splitClip = useCallback((clipId: string, splitTime: number) => {
    dispatch({ type: 'SPLIT_CLIP', clipId, splitTime })
  }, [])

  const trimClip = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_CLIP', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const trimClipPreview = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_CLIP_PREVIEW', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const setClipOffset = useCallback((clipId: string, startOffset: number) => {
    dispatch({ type: 'SET_CLIP_OFFSET', clipId, startOffset })
  }, [])

  const setClipTrackIndex = useCallback((clipId: string, trackIndex: number) => {
    dispatch({ type: 'SET_CLIP_TRACK_INDEX', clipId, trackIndex })
  }, [])

  const selectClip = useCallback((clipId: string | null, clipType?: 'video' | 'audio' | null) => {
    dispatch({ type: 'SELECT_CLIP', clipId, clipType })
  }, [])

  const toggleClipSelection = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_CLIP_SELECTION', clipId })
  }, [])

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' })
  }, [])

  const rippleDeleteSelected = useCallback(() => {
    dispatch({ type: 'RIPPLE_DELETE_SELECTED' })
  }, [])

  const multiDragOffset = useCallback((deltaSec: number, trackDelta?: number) => {
    dispatch({ type: 'MULTI_DRAG_OFFSET', deltaSec, trackDelta })
  }, [])

  const setPlayhead = useCallback((position: number) => {
    dispatch({ type: 'SET_PLAYHEAD', position })
  }, [])

  const setZoom = useCallback((zoomLevel: number) => {
    dispatch({ type: 'SET_ZOOM', zoomLevel })
  }, [])

  const play = useCallback(() => {
    dispatch({ type: 'PLAY' })
  }, [])

  const pause = useCallback(() => {
    dispatch({ type: 'PAUSE' })
  }, [])

  const togglePlayPause = useCallback(() => {
    dispatch({ type: state.present.isPlaying ? 'PAUSE' : 'PLAY' })
  }, [state.present.isPlaying])

  const addAudioClip = useCallback((clip: TimelineAudioClip) => {
    dispatch({ type: 'ADD_AUDIO_CLIP', clip })
  }, [])

  const addAudioClips = useCallback((clips: TimelineAudioClip[]) => {
    dispatch({ type: 'ADD_AUDIO_CLIPS', clips })
  }, [])

  const removeAudioClip = useCallback((clipId: string) => {
    dispatch({ type: 'REMOVE_AUDIO_CLIP', clipId })
  }, [])

  const setAudioVolume = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_AUDIO_VOLUME', clipId, volume })
  }, [])

  const toggleAudioMute = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_AUDIO_MUTE', clipId })
  }, [])

  const setAudioOffset = useCallback((clipId: string, startOffset: number) => {
    dispatch({ type: 'SET_AUDIO_OFFSET', clipId, startOffset })
  }, [])

  const setAudioTrackIndex = useCallback((clipId: string, trackIndex: number) => {
    dispatch({ type: 'SET_AUDIO_TRACK_INDEX', clipId, trackIndex })
  }, [])

  const trimAudioClip = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_AUDIO_CLIP', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const trimAudioClipPreview = useCallback(
    (clipId: string, trimStart: number, trimEnd: number, startOffset?: number) => {
      dispatch({ type: 'TRIM_AUDIO_CLIP_PREVIEW', clipId, trimStart, trimEnd, startOffset })
    },
    []
  )

  const setClipSpeed = useCallback((clipId: string, speed: number) => {
    dispatch({ type: 'SET_CLIP_SPEED', clipId, speed })
  }, [])

  const setClipVolume = useCallback((clipId: string, volume: number) => {
    dispatch({ type: 'SET_CLIP_VOLUME', clipId, volume })
  }, [])

  const setClipFadeOut = useCallback((clipId: string, fadeOutS: number) => {
    dispatch({ type: 'SET_CLIP_FADE_OUT', clipId, fadeOutS })
  }, [])

  const setClipFadeIn = useCallback((clipId: string, fadeInS: number) => {
    dispatch({ type: 'SET_CLIP_FADE_IN', clipId, fadeInS })
  }, [])

  const normalizeTimelineOrigin = useCallback(() => {
    dispatch({ type: 'NORMALIZE_TIMELINE_ORIGIN' })
  }, [])

  const closeAllGaps = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_GAPS' })
  }, [])

  const toggleClipMute = useCallback((clipId: string) => {
    dispatch({ type: 'TOGGLE_CLIP_MUTE', clipId })
  }, [])

  const setAudioSpeed = useCallback((clipId: string, speed: number) => {
    dispatch({ type: 'SET_AUDIO_SPEED', clipId, speed })
  }, [])

  const splitAudioClip = useCallback((clipId: string, splitTime: number) => {
    dispatch({ type: 'SPLIT_AUDIO_CLIP', clipId, splitTime })
  }, [])

  const reorderAudioClip = useCallback((clipId: string, newOrder: number) => {
    dispatch({ type: 'REORDER_AUDIO_CLIP', clipId, newOrder })
  }, [])

  const copyClip = useCallback(() => {
    dispatch({ type: 'COPY_CLIP' })
  }, [])

  const pasteClip = useCallback(() => {
    dispatch({ type: 'PASTE_CLIP' })
  }, [])

  const deleteSelectedClip = useCallback(() => {
    dispatch({ type: 'DELETE_SELECTED_CLIP' })
  }, [])

  const splitSelectedClip = useCallback(() => {
    dispatch({ type: 'SPLIT_SELECTED_CLIP' })
  }, [])

  const addVideoTrack = useCallback(() => {
    dispatch({ type: 'ADD_VIDEO_TRACK' })
  }, [])

  const addAudioTrack = useCallback(() => {
    dispatch({ type: 'ADD_AUDIO_TRACK' })
  }, [])

  // RFC-0631 round-3 (Issue 5): Remove a sub-track. The caller is
  // responsible for the user confirmation when clips exist on the track —
  // the reducer guards on `deleteClips` so accidental dispatches without a
  // confirm are a no-op.
  const removeVideoTrack = useCallback(
    (trackIndex: number, deleteClips: boolean) => {
      trackMgmtLog({
        event: 'track-remove-requested',
        kind: 'video',
        trackIndex,
        deleteClips,
      })
      dispatch({ type: 'REMOVE_VIDEO_TRACK', trackIndex, deleteClips })
    },
    [],
  )

  const removeAudioTrack = useCallback(
    (trackIndex: number, deleteClips: boolean) => {
      trackMgmtLog({
        event: 'track-remove-requested',
        kind: 'audio',
        trackIndex,
        deleteClips,
      })
      dispatch({ type: 'REMOVE_AUDIO_TRACK', trackIndex, deleteClips })
    },
    [],
  )

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' })
  }, [])

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' })
  }, [])

  const restoreState = useCallback((newState: TimelineState) => {
    dispatch({ type: 'RESTORE_STATE', state: newState })
  }, [])

  const toggleTrackMute = useCallback(
    (trackKind: 'video' | 'audio', trackIndex: number) => {
      dispatch({ type: 'TOGGLE_TRACK_MUTE', trackKind, trackIndex })
    },
    [],
  )

  const setMagnetEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_MAGNET', enabled })
  }, [])

  const magnetPackTrack0 = useCallback(() => {
    dispatch({ type: 'MAGNET_PACK_TRACK0' })
  }, [])

  const extractAudioFromClip = useCallback(
    (sourceClipId: string, audioClip: TimelineAudioClip, newAudioTrackIndex: number) => {
      dispatch({
        type: 'EXTRACT_AUDIO_FROM_CLIP',
        sourceClipId,
        audioClip,
        newAudioTrackIndex,
      })
    },
    [],
  )

  // Derived state
  const canUndo = state.past.length > 0
  const canRedo = state.future.length > 0

  return useMemo(
    () => ({
      // Current state
      timelineState: state.present,

      // Actions
      addClip,
      removeClip,
      reorderClip,
      splitClip,
      trimClip,
      trimClipPreview,
      setClipOffset,
      setClipTrackIndex,
      selectClip,
      toggleClipSelection,
      clearSelection,
      rippleDeleteSelected,
      multiDragOffset,
      setPlayhead,
      setZoom,
      play,
      pause,
      togglePlayPause,
      addAudioClip,
      addAudioClips,
      removeAudioClip,
      setAudioVolume,
      toggleAudioMute,
      setAudioOffset,
      setAudioTrackIndex,
      trimAudioClip,
      trimAudioClipPreview,
      setClipSpeed,
      setClipVolume,
      setClipFadeOut,
      setClipFadeIn,
      normalizeTimelineOrigin,
      closeAllGaps,
      toggleClipMute,
      setAudioSpeed,
      splitAudioClip,
      reorderAudioClip,
      copyClip,
      pasteClip,
      deleteSelectedClip,
      splitSelectedClip,
      addVideoTrack,
      addAudioTrack,
      removeVideoTrack,
      removeAudioTrack,
      undo,
      redo,
      restoreState,
      toggleTrackMute,
      setMagnetEnabled,
      magnetPackTrack0,
      extractAudioFromClip,

      // Undo/Redo state
      canUndo,
      canRedo,
    }),
    [
      state.present,
      addClip,
      removeClip,
      reorderClip,
      splitClip,
      trimClip,
      trimClipPreview,
      setClipOffset,
      setClipTrackIndex,
      selectClip,
      toggleClipSelection,
      clearSelection,
      rippleDeleteSelected,
      multiDragOffset,
      setPlayhead,
      setZoom,
      play,
      pause,
      togglePlayPause,
      addAudioClip,
      addAudioClips,
      removeAudioClip,
      setAudioVolume,
      toggleAudioMute,
      setAudioOffset,
      setAudioTrackIndex,
      trimAudioClip,
      trimAudioClipPreview,
      setClipSpeed,
      setClipVolume,
      setClipFadeOut,
      setClipFadeIn,
      normalizeTimelineOrigin,
      closeAllGaps,
      toggleClipMute,
      setAudioSpeed,
      splitAudioClip,
      reorderAudioClip,
      copyClip,
      pasteClip,
      deleteSelectedClip,
      splitSelectedClip,
      addVideoTrack,
      addAudioTrack,
      removeVideoTrack,
      removeAudioTrack,
      undo,
      redo,
      restoreState,
      toggleTrackMute,
      setMagnetEnabled,
      magnetPackTrack0,
      extractAudioFromClip,
      canUndo,
      canRedo,
    ]
  )
}
